import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * TrialCountdown — small "N days left in your free trial" widget.
 *
 * Reads `GET /api/me/trial-status`. Renders nothing when the caller isn't on a
 * trial (grandfathered users, expired trials, paying members).
 *
 * Variants:
 *  - `variant="compact"` — one-line pill for LeftRail / mobile drawer.
 *  - `variant="card"`    — full block for Profile / dashboard.
 */
export default function TrialCountdown({ variant = "compact", className = "" }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/trial-status");
        if (!cancelled) setState(data);
      } catch {
        /* silent — widget just hides */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state || !state.is_on_trial) return null;

  const days = state.days_remaining ?? 0;
  const hours = state.hours_remaining ?? 0;
  const label = days > 0
    ? `${days} day${days === 1 ? "" : "s"} left`
    : `${hours} hour${hours === 1 ? "" : "s"} left`;

  if (variant === "card") {
    return (
      <div
        data-testid="trial-countdown-card"
        className={`border border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-black to-black px-5 py-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
          <Sparkles size={11} /> Standard trial
        </div>
        <div className="font-display text-2xl mb-1" data-testid="trial-countdown-days">
          {label}
        </div>
        <div className="text-xs text-zinc-400 leading-relaxed">
          You're on a 30-day free trial of My Panda X Standard. Enjoy the full
          experience — no charge, no auto-renew.
        </div>
        <Link
          to="/faq/members"
          className="mt-3 inline-block text-[11px] uppercase tracking-widest text-amber-400 hover:text-amber-300"
          data-testid="trial-countdown-learn"
        >
          What's included →
        </Link>
      </div>
    );
  }

  return (
    <Link
      to="/faq/members"
      data-testid="trial-countdown-pill"
      className={`flex items-center justify-between gap-2 px-3 py-2 border border-amber-400/30 hover:border-amber-400 text-amber-400 text-xs transition-colors ${className}`}
    >
      <span className="flex items-center gap-1.5">
        <Clock size={12} /> Trial
      </span>
      <span data-testid="trial-countdown-label">{label}</span>
    </Link>
  );
}
